import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Form, Nav, Button, InputGroup, Offcanvas } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Page from "../../Page";
import {
  Search,
  PlusCircle,
  CloudUpload,
  Pencil,
  Eye,
  Copy,
  Trash,
  Calendar3,
  Megaphone,
  Upload,
  Funnel,
  SortDown,
  Pause,
  Play,
} from "react-bootstrap-icons";
import { fetchContestList, fetchContest, copyContest, deleteContest, pauseContest } from "../../../redux/actions/ContestAction";
import Loading from "../../Loading";
import Pagination from "../../shared/Pagination";
import "./Contests.css";

const STATUS_MAP = {
  active: { label: "Active", class: "status-active" },
  draft: { label: "Draft", class: "status-draft" },
  completed: { label: "Completed", class: "status-completed" },
  paused: { label: "Paused", class: "status-paused" },
};

const ELIGIBLE_USER_TYPES = {
  "1": "All Active Agents",
  "2": "Specific Category",
};

// Contest type tag (Trip / Cash / Gift)
const TYPE_TAG_CLASS = "contest-type-tag";

// Default placeholder when contest has no banner image
const DEFAULT_CONTEST_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200' viewBox='0 0 400 200'%3E%3Crect fill='%23f3f4f6' width='400' height='200'/%3E%3Cg fill='%239ca3af' opacity='0.6'%3E%3Cpath d='M160 70h80v60h-80z'/%3E%3Ccircle cx='185' cy='95' r='15'/%3E%3Cpath d='M165 120l25-25 15 15 35-35 20 20v35H165z'/%3E%3C/g%3E%3Ctext x='200' y='165' fill='%239ca3af' font-size='14' font-family='sans-serif' text-anchor='middle'%3ENo image%3C/text%3E%3C/svg%3E";

function formatCompact(num) {
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(num);
}

function formatWithCommas(num) {
  return Number(num).toLocaleString();
}

/** Normalize list API pause fields to 0 (running) or 1 (paused). */
function contestPauseState(raw) {
  if (raw === 1 || raw === "1" || raw === true || raw === "true") return 1;
  if (typeof raw === "string" && raw.toLowerCase() === "paused") return 1;
  return 0;
}

const LEADERBOARD_TYPE_LABELS = {
  "1": "Overall Ranking",
  "2": "Category-wise",
  "3": "Region-wise",
};

function formatLeaderboardType(raw) {
  if (raw === null || raw === undefined || raw === "") return "-";
  const key = String(raw).trim();
  return LEADERBOARD_TYPE_LABELS[key] ?? raw;
}

function isContestLeaderboardEnabled(detail) {
  if (!detail) return false;
  if (detail.is_leaderboard === 1 || detail.is_leaderboard === true || String(detail.is_leaderboard) === "1") {
    return true;
  }
  if (detail.leaderboard_link === 1 || detail.leaderboard_link === true) return true;
  const lt = detail.leaderboard_type;
  if (lt === undefined || lt === null || String(lt).trim() === "") return false;
  const s = String(lt).trim();
  return s === "1" || s === "2" || s === "3" || [1, 2, 3].includes(Number(s));
}

const ContestListing = (props) => {
  props.pageTypeHandler?.("Contests");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const authToken = props.authToken;

  // Get contest data from Redux store
  const contestListData = useSelector((state) => state.contest.contestList);
  const isLoading = useSelector((state) => state.contest.loading);
  const contestDetail = useSelector((state) => state.contest.contest);

  const DEFAULT_LIMIT = 20;
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [bannerFile, setBannerFile] = useState(null);
  const [contests, setContests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIMIT);
  const [filterContestType, setFilterContestType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const bannerInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const isFirstSearchEffectRef = useRef(true);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);

  // Map tab to API filter value (all -> "", drafts -> "draft")
  const filterValue = activeTab === "all" ? "" : activeTab === "drafts" ? "draft" : activeTab;
  // API contest_type filter (Trip, Cash, Gift or empty)
  const contestTypeForApi = filterContestType || "";

  // Fetch contest list with payload: { search, page, limit, status, contest_type, fromdate, todate, sort_type, sort }
  const loadContestList = useCallback(
    (searchTerm, pageNum = 1, status = "", contestType = "", limit, fromDate = "", toDate = "", sortType = "", sortOrder = "") => {
      if (authToken) {
        dispatch(fetchContestList(authToken, searchTerm, pageNum, limit ?? itemsPerPage, status, contestType, fromDate, toDate, sortType, sortOrder));
      }
    },
    [authToken, dispatch, itemsPerPage]
  );

  // Initial fetch on mount
  useEffect(() => {
    loadContestList("", 1, "", "", undefined, "", "", "", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API call when search input or tab (filter) changes - debounced for search
  useEffect(() => {
    if (isFirstSearchEffectRef.current) {
      isFirstSearchEffectRef.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      loadContestList(search.trim(), 1, filterValue, contestTypeForApi, undefined, filterDateFrom, filterDateTo, sortBy, sortOrder);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, filterValue, contestTypeForApi, filterDateFrom, filterDateTo, sortBy, sortOrder, loadContestList]);

  // Update contests when contestListData changes
  useEffect(() => {
    if (contestListData && contestListData.data) {
      const apiData = contestListData.data;
      if (Array.isArray(apiData)) {
        // Map API response fields to component expected fields
        const mappedContests = apiData.map((contest) => ({
          id: String(contest.id),
          name: contest.contest_name,
          type: contest.frequency,
          contestType: contest.contest_type,
          status: contest.status,
          startDate: contest.start_date,
          endDate: contest.end_date,
          participants: contest.total_participants || 0,
          goalCompleted: Number.parseInt(String(contest.goal_progress ?? "").replace("%", "").trim(), 10) || 0,
          imageUrl: contest.banner_image || null,
          subtitle: contest.subtitle,
          clientId: contest.client_id,
          pause: contestPauseState(
            contest.pause ?? contest.is_pause ?? contest.is_paused ?? contest.paused
          ),
        }));
        setContests(mappedContests);
      }
    }
  }, [contestListData]);

  // When returning from Create/Edit page with saved contest
  useEffect(() => {
    const state = location.state;
    if (!state?.savedContest) return;
    const formData = state.savedContest;
    const editId = state.editId;
    setContests((prev) => {
      if (editId) {
        return prev.map((c) =>
          c.id === editId
            ? {
                ...c,
                name: formData.contestName,
                type: formData.contestType || "Campaign",
                contestType: formData.contestType,
                status: formData.status || "draft",
                startDate: formData.startDate,
                endDate: formData.endDate,
                pause: c.pause ?? 0,
              }
            : c
        );
      }
      return [
        {
          id: String(Date.now()),
          name: formData.contestName,
          type: formData.contestType || "Campaign",
          contestType: formData.contestType,
          status: formData.status || "draft",
          startDate: formData.startDate,
          endDate: formData.endDate,
          participants: 0,
          goalCompleted: 0,
          imageUrl: null,
          pause: 0,
        },
        ...prev,
      ];
    });
    navigate("/contests", { replace: true, state: {} });
  }, [location.state, navigate]);

  // Summary stats from API response
  const summary = useMemo(() => {
    const apiSummary = contestListData || {};
    const totalParticipants = contests.reduce((s, c) => s + (c.participants || 0), 0);
    return {
      activeContests: apiSummary.active_contests || contests.filter((c) => c.status === "active").length,
      totalParticipants: totalParticipants,
      totalParticipantsFormatted: apiSummary.total_participants_new || "0",
      completionRate: apiSummary.completion_rate || "0%",
      rewardsClaimed: apiSummary.rewards_claimed || 0,
      totalContests: apiSummary.total_contests || contests.length,
      draftContests: apiSummary.draft_contests || contests.filter((c) => c.status === "draft").length,
      completedContests: apiSummary.completed_contests || contests.filter((c) => c.status === "completed").length,
    };
  }, [contests, contestListData]);

  const tabCounts = useMemo(() => {
    const apiSummary = contestListData || {};
    return {
      active: apiSummary.active_contests ?? contests.filter((c) => c.status === "active").length,
      drafts: apiSummary.draft_contests ?? contests.filter((c) => c.status === "draft").length,
      completed: apiSummary.completed_contests ?? contests.filter((c) => c.status === "completed").length,
    };
  }, [contests, contestListData]);

  const filteredContests = useMemo(() => {
    let list = contests;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q));
    }
    if (activeTab !== "all") {
      const statusToFilter = activeTab === "drafts" ? "draft" : activeTab;
      list = list.filter((c) => c.status === statusToFilter);
    }
    if (filterContestType) {
      list = list.filter((c) => (c.contestType || c.type || "") === filterContestType);
    }
    if (filterDateFrom) {
      list = list.filter((c) => c.startDate && new Date(c.startDate) >= new Date(filterDateFrom));
    }
    if (filterDateTo) {
      list = list.filter((c) => c.startDate && new Date(c.startDate) <= new Date(filterDateTo));
    }
    const sorted = [...list].sort((a, b) => {
      let comparison = 0;
      if (sortBy === "date") {
        comparison = new Date(a.startDate || 0) - new Date(b.startDate || 0);
      } else if (sortBy === "participants") {
        comparison = (a.participants || 0) - (b.participants || 0);
      } else if (sortBy === "progress") {
        comparison = (a.goalCompleted || 0) - (b.goalCompleted || 0);
      }
      return sortOrder === "desc" ? -comparison : comparison;
    });
    return sorted;
  }, [contests, search, activeTab, filterContestType, filterDateFrom, filterDateTo, sortBy, sortOrder]);

  // Slice to show only current page according to "per page" selection
  const displayedContests = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredContests.slice(start, start + itemsPerPage);
  }, [filteredContests, currentPage, itemsPerPage]);

  const handleUploadCSV = (contest) => {
    navigate(`/contests/import/${contest.id}`, { state: { contest } });
  };

  const handleSearchSubmit = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setCurrentPage(1);
    loadContestList(search.trim(), 1, filterValue, contestTypeForApi, undefined, filterDateFrom, filterDateTo, sortBy, sortOrder);
  };

  // Pagination totals: use filtered list length when filters/sort are applied, else API tab counts
  const totalItems = useMemo(() => {
    const hasClientFilters = filterContestType || filterDateFrom || filterDateTo;
    if (hasClientFilters) return filteredContests.length;
    const api = contestListData || {};
    if (activeTab === "all") return api.total_contests ?? filteredContests.length;
    if (activeTab === "active") return api.active_contests ?? tabCounts.active ?? filteredContests.length;
    if (activeTab === "drafts") return api.draft_contests ?? tabCounts.drafts ?? filteredContests.length;
    if (activeTab === "completed") return api.completed_contests ?? tabCounts.completed ?? filteredContests.length;
    return filteredContests.length;
  }, [activeTab, contestListData, tabCounts, filteredContests.length, filterContestType, filterDateFrom, filterDateTo]);

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
    loadContestList(search.trim(), pageNum, filterValue, contestTypeForApi, undefined, filterDateFrom, filterDateTo, sortBy, sortOrder);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
    loadContestList(search.trim(), 1, filterValue, contestTypeForApi, newLimit, filterDateFrom, filterDateTo, sortBy, sortOrder);
  };

  const handleFilterTypeChange = (e) => {
    setFilterContestType(e.target.value || "");
    setCurrentPage(1);
  };
  const handleFilterDateFromChange = (e) => {
    setFilterDateFrom(e.target.value || "");
    setCurrentPage(1);
  };
  const handleFilterDateToChange = (e) => {
    setFilterDateTo(e.target.value || "");
    setCurrentPage(1);
  };
  const handleSortByChange = (e) => {
    setSortBy(e.target.value || "date");
    setCurrentPage(1);
  };
  const handleSortOrderChange = (e) => {
    setSortOrder(e.target.value || "desc");
    setCurrentPage(1);
  };

  const handleEdit = (contest) => {
    navigate(`/contests/edit/${contest.id}`, { state: { contest } });
  };

  const handleDuplicate = (contest) => {
    const refreshList = () => loadContestList(search.trim(), currentPage, filterValue, contestTypeForApi, undefined, filterDateFrom, filterDateTo, sortBy, sortOrder);
    dispatch(copyContest(authToken, contest.id, refreshList));
  };

  const handleDelete = (contest) => {
    if (window.confirm(`Delete contest "${contest.name}"?`)) {
      const refreshList = () => loadContestList(search.trim(), currentPage, filterValue, contestTypeForApi, undefined, filterDateFrom, filterDateTo, sortBy, sortOrder);
      dispatch(deleteContest(authToken, contest.id, refreshList));
    }
  };

  const handlePause = (contest) => {
    const refreshList = () =>
      loadContestList(
        search.trim(),
        currentPage,
        filterValue,
        contestTypeForApi,
        undefined,
        filterDateFrom,
        filterDateTo,
        sortBy,
        sortOrder
      );
    const currentlyPaused = contestPauseState(contest.pause) === 1;
    // API: is_pause 1 = pause contest, 0 = resume
    const isPause = currentlyPaused ? 0 : 1;
    dispatch(pauseContest(authToken, contest.id, isPause, refreshList));
  };

  const handleViewDetails = (contest) => {
    dispatch(fetchContest(authToken, contest.id));
    setShowDetailDrawer(true);
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-");
  const formatDateShort = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "-");
  const formatDateRange = (start, end) => {
    if (!start) return "-";
    if (!end) return `Starts ${formatDate(start)}`;
    const startY = start ? new Date(start).getFullYear() : null;
    const endY = end ? new Date(end).getFullYear() : null;
    if (startY === endY) return `${formatDateShort(start)} - ${formatDateShort(end)}, ${endY}`;
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const formatFrequencyLabel = (freq) => {
    if (!freq) return "-";
    if (freq === "one-time" || freq === "campaign") return "Campaign (One-time)";
    if (freq === "monthly") return "Monthly";
    if (freq === "quarterly") return "Quarterly";
    return freq;
  };

  return (
    <Page title={props.title || "Contests"}>
      {isLoading && <Loading />}
      <div className="contests-page">
        <div className="page-header-row">
          <h1 className="page-title">Contests</h1>
          <div className="search-create-row">
            <InputGroup className="contests-search-input">
              <InputGroup.Text className="contests-search-icon">
                <Search size={18} />
              </InputGroup.Text>
              <Form.Control
                type="search"
                placeholder="Search contests..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
              />
            </InputGroup>

            <Button
              className="btn-create-contest btn-purple"
              onClick={() => navigate("/contests/create")}
            >
              <PlusCircle className="me-1" size={18} />
              Create Contest
            </Button>
          </div>
        </div>
        <div className="contests-filter-sort-row">
          <div className="contests-filter-group">
            <span className="contests-filter-sort-label">
              <Funnel size={16} className="me-1" />
              Filter
            </span>
            <Form.Select
              className="contests-filter-select"
              value={filterContestType}
              onChange={handleFilterTypeChange}
              aria-label="Contest type"
            >
              <option value="">All types</option>
              <option value="Trip">Trip</option>
              <option value="Cash">Cash</option>
              <option value="Gift">Gift</option>
            </Form.Select>
            <Form.Control
              type="date"
              className="contests-filter-date"
              placeholder="From"
              value={filterDateFrom}
              onChange={handleFilterDateFromChange}
              aria-label="Date from"
            />
            <Form.Control
              type="date"
              className="contests-filter-date"
              placeholder="To"
              value={filterDateTo}
              onChange={handleFilterDateToChange}
              aria-label="Date to"
            />
          </div>
          <div className="contests-sort-group">
            <span className="contests-filter-sort-label">
              <SortDown size={16} className="me-1" />
              Sort
            </span>
            <Form.Select
              className="contests-sort-select"
              value={sortBy}
              onChange={handleSortByChange}
              aria-label="Sort by"
            >
              <option value="date">Date</option>
              <option value="participants">Participants</option>
              <option value="progress">Progress</option>
            </Form.Select>
            <Form.Select
              className="contests-sort-order-select"
              value={sortOrder}
              onChange={handleSortOrderChange}
              aria-label="Sort order"
            >
              <option value="desc">{sortBy === "date" ? "Newest first" : "High to low"}</option>
              <option value="asc">{sortBy === "date" ? "Oldest first" : "Low to high"}</option>
            </Form.Select>
          </div>
        </div>
        <div className="contests-summary-row">
          <div className="contests-summary-box">
            <div className="label">Active Contests</div>
            <div className="value">{summary.activeContests || 0}</div>
          </div>
          <div className="contests-summary-box">
            <div className="label">Total Participants</div>
            <div className="value">{summary.totalParticipantsFormatted || "0"}</div>
          </div>
          <div className="contests-summary-box">
            <div className="label">Completion Rate</div>
            <div className="value">{summary.completionRate || "0%"}</div>
          </div>
          <div className="contests-summary-box">
            <div className="label">Rewards Claimed</div>
            <div className="value">{summary.rewardsClaimed || 0}</div>
          </div>
        </div>

        <div className="contests-tabs-row">
          <Nav variant="tabs" className="contests-tabs">
            <Nav.Item>
              <Nav.Link
                active={activeTab === "all"}
                onClick={() => setActiveTab("all")}
                className="nav-link-tab"
              >
                All Contests ({summary.totalContests})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === "active"}
                onClick={() => setActiveTab("active")}
                className="nav-link-tab"
              >
                Active ({tabCounts.active})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === "drafts"}
                onClick={() => setActiveTab("drafts")}
                className="nav-link-tab"
              >
                Drafts ({tabCounts.drafts})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === "completed"}
                onClick={() => setActiveTab("completed")}
                className="nav-link-tab"
              >
                Completed ({tabCounts.completed})
              </Nav.Link>
            </Nav.Item>
          </Nav>
          {bannerFile && (
            <span className="text-muted small ms-1">({bannerFile.name})</span>
          )}
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            className="d-none"
            onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
          />
        </div>

     

        <div className="contest-list">
          {filteredContests.length === 0 ? (
            <p className="text-muted py-4 text-center">No contests found.</p>
          ) : (
            displayedContests.map((contest) => {
              

              const isPaused = contestPauseState(contest.pause) === 1;
              const statusInfo = isPaused
                ? STATUS_MAP.paused
                : STATUS_MAP[contest.status] || STATUS_MAP.draft;

             // const statusInfo = STATUS_MAP[contest.status] || STATUS_MAP.draft;
              const typeLabel = contest.contestType || contest.type;
           //   const isPaused = contestPauseState(contest.pause) === 1;
              const isActive = contest.status === "active";
              const isDraft = contest.status === "draft";
              const isCompleted = contest.status === "completed";

          return (
                <div key={contest.id} className="contest-card">
                  <div className="contest-image-col">
                    {contest.imageUrl ? (
                      <img src={contest.imageUrl} alt={contest.name} />
                    ) : (
                      <img
                        src={DEFAULT_CONTEST_IMAGE}
                        alt=""
                        className="contest-image-placeholder-img"
                      />
                    )}
                  </div>
                  <div className="contest-body">
                    <div className="contest-tags">
                      <span className={`status-badge ${statusInfo.class}`}>
                        {statusInfo.label}
                      </span>
                      <span className={TYPE_TAG_CLASS}>{typeLabel}</span>
                    </div>
                    <div className="contest-name">{contest.name}</div>
                    <div className="contest-detail-lines">
                      <span className="contest-detail-line ucfirst">
                        <Megaphone size={14} className="me-1" />
                        {(contest.type || "")}
                      </span>
                      <span className="contest-detail-line">
                        <Calendar3 size={14} className="me-1" />
                        {formatDateRange(contest.startDate, contest.endDate)}
                      </span>
                    </div>
                  </div>
                  <div className="contest-right">
                    {contest.status === "active" && (
                      <div className="contest-new-stats">
                        <div className="contest-stat">
                          <div className="contest-stat-header mb-2">
                            <span className="contest-stat-label">Participants</span>
                            <span className="contest-stat-value contest-stat-value-muted">
                              {formatWithCommas(contest.participants ?? 0)}
                            </span>
                          </div>
                        </div>
                        <div className="contest-stat">
                          <div className="contest-stat-header">
                            <span className="contest-stat-label">Goal Progress</span>
                            <span className="contest-progress-pct contest-progress-pct-accent">
                              {contest.goalCompleted || 0}%
                            </span>
                          </div>
                          <div className="contest-progress-bar">
                            <div className="progress">
                              <div
                                className="progress-bar progress-bar-purple"
                                style={{ width: `${contest.goalCompleted || 0}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {contest.status === "draft" && (
                      <div className="contest-new-stats">
                        <div className="contest-setup-msg mb-2">Setup not completed</div>
                        <div className="progress">
                          <div className="progress-bar progress-bar-gray" style={{ width: "5%" }} />
                        </div>
                      </div>
                    )}
                    {contest.status === "completed" && (
                      <div className="contest-new-stats">
                        <div className="contest-stat">
                          <span className="contest-stat-label">Final Count</span>
                          <span className="contest-stat-value">{formatWithCommas(contest.participants ?? 0)}</span>
                        </div>
                        <div className="progress">
                          <div className="progress-bar progress-bar-gray" style={{ width: "100%" }} />
                        </div>
                      </div>
                    )}
                    <div className="contest-actions">
                      <div className="contest-actions-row">
                        <button
                          type="button"
                          className="btn-icon"
                          title="Upload progress"
                          onClick={() => handleUploadCSV(contest)}
                          disabled={!isActive || isPaused}
                        >
                          <CloudUpload size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title="Edit"
                          onClick={() => handleEdit(contest)}
                          disabled={isCompleted}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title="View"
                          onClick={() => handleViewDetails(contest)}
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                      <div className="contest-actions-row">
                        <button
                          type="button"
                          className="btn-icon"
                          title="Duplicate"
                          onClick={() => handleDuplicate(contest)}
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title={isPaused ? "Resume" : "Pause"}
                          onClick={() => handlePause(contest)}
                          disabled={!isActive}
                          aria-label={isPaused ? "Resume contest" : "Pause contest"}
                        >
                          {isPaused ? <Play size={16} /> : <Pause size={16} />}
                        </button>


                        <button
                          type="button"
                          className="btn-icon btn-icon-danger"
                          title="Delete"
                          onClick={() => handleDelete(contest)}
                          disabled={!isDraft}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            showInfo={true}
          />
        )}

        <Offcanvas
          show={showDetailDrawer}
          onHide={() => setShowDetailDrawer(false)}
          placement="end"
          className="contest-detail-drawer"
        >
          <Offcanvas.Body>
            {isLoading && !contestDetail ? (
              <div className="text-center text-muted">Loading contest...</div>
            ) : !contestDetail ? (
              <div className="text-muted">No contest details available.</div>
            ) : (
              <div className="contest-detail-content">
                {/* Basic Contest Details */}
                <div className="contest-detail-header">
                  {contestDetail.banner_image && (
                    <div className="contest-detail-image-wrap">
                      <img
                        src={contestDetail.banner_image}
                        alt={contestDetail.contest_name || contestDetail.name}
                        className="contest-detail-image"
                      />
                    </div>
                  )}
                </div>

                <div className="contest-detail-section-title">Basic Contest Details</div>
                <div className="contest-detail-section-grid">
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Contest Name</div>
                    <div className="contest-detail-value">
                      {contestDetail.contest_name || contestDetail.name || "-"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Subtitle</div>
                    <div className="contest-detail-value">
                      {contestDetail.subtitle || "-"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Contest Type</div>
                    <div className="contest-detail-value">
                      {contestDetail.contest_type || "-"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Category</div>
                    <div className="contest-detail-value">
                      {contestDetail.category || "Sales & Performance"}
                    </div>
                  </div>
                </div>

                {contestDetail.description && (
                  <div className="contest-detail-section mt-2">
                    <div className="contest-detail-label">Description</div>
                    <div className="contest-detail-value">{contestDetail.description}</div>
                  </div>
                )}

                {/* Duration & Frequency */}
                <div className="contest-detail-section-title mt-3">Duration & Frequency</div>
                <div className="contest-detail-section-grid">
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Start Date</div>
                    <div className="contest-detail-value">
                      {contestDetail.start_date ? formatDate(contestDetail.start_date) : "-"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">End Date</div>
                    <div className="contest-detail-value">
                      {contestDetail.end_date ? formatDate(contestDetail.end_date) : "-"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Frequency</div>
                    <div className="contest-detail-value">
                      {formatFrequencyLabel(contestDetail.frequency)}Monthly
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Reset Progress</div>
                    <div className="contest-detail-value">
                      {contestDetail.reset_progress ? "Yes" : "No"}
                    </div>
                  </div>
                </div>

                {/* Progress & Scoring */}
                <div className="contest-detail-section-title mt-3">Progress & Scoring</div>
                <div className="contest-detail-section-grid">
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Progress Metric</div>
                    <div className="contest-detail-value">
                      {contestDetail.progress_metric || "-"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Target Value</div>
                    <div className="contest-detail-value">
                      {contestDetail.target_value != null
                        ? formatWithCommas(contestDetail.target_value)
                        : "-"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Display Label</div>
                    <div className="contest-detail-value">
                      {contestDetail.display_label || "Revenue to go"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Minimum Threshold</div>
                    <div className="contest-detail-value">
                      {contestDetail.minimum_threshold != null
                        ? formatWithCommas(contestDetail.minimum_threshold)
                        : "-"}
                    </div>
                  </div>
                </div>

                {/* Leaderboard & Eligibility */}
                <div className="contest-detail-section-title mt-3">Leaderboard & Eligibility</div>
                <div className="contest-detail-section-grid">
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Leaderboard Enabled</div>
                    <div className="contest-detail-value">
                      {isContestLeaderboardEnabled(contestDetail) ? "Yes" : "No"}
                    </div>
                  </div>
                  <div className="contest-detail-section">
                    <div className="contest-detail-label">Leaderboard Type</div>
                    <div className="contest-detail-value">
                      {formatLeaderboardType(contestDetail.leaderboard_type)}
                    </div>
                  </div>
                </div>

                <div className="contest-detail-section mt-3">
                        <div className="contest-detail-label">Eligible Users</div>
                        <div className="contest-detail-value">
                      {contestDetail.eligible_users == 1
                        ? ELIGIBLE_USER_TYPES[contestDetail.eligible_users]
                        : Array.isArray(contestDetail.partnerCatname)
                        ? contestDetail.partnerCatname
                            .map((item) => item?.partner_category)
                            .filter(Boolean)
                            .join(", ")
                        : contestDetail.partnerCatname || "-"}
                        </div>
                </div>


                {/* Milestones & Rewards */}
                {Array.isArray(contestDetail.milestones) &&
                  contestDetail.milestones.length > 0 && (
                    <div className="mt-3">
                      <div className="contest-detail-section-title">Milestones & Rewards</div>
                      <div className="contest-detail-milestones">
                        {contestDetail.milestones.map((m, idx) => (
                          <div key={idx} className="contest-detail-milestone">
                            <div className="contest-detail-milestone-header">
                              <span className="contest-detail-milestone-name">
                                {m.milestone_name || `Milestone ${idx + 1}`}
                              </span>
                              {m.target_percentage != null && (
                                <span className="contest-detail-milestone-badge">
                                  {m.target_percentage}%
                                </span>
                              )}
                            </div>
                            <div className="contest-detail-milestone-body">
                              <div>
                                <span className="contest-detail-label">Reward Type: </span>
                                <span className="contest-detail-value-inline">
                                  {m.reward_type || "-"}
                                </span>
                              </div>
                              {m.reward_details && (
                                <div className="mt-1">
                                  <span className="contest-detail-label">Details: </span>
                                  <span className="contest-detail-value-inline">
                                    {m.reward_details}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </div>

    </Page>
  );
};

export default ContestListing;
